import { PERMISSIONS } from "@/constants/permissions"
import { useDataGapsStats } from "@/features/material-compliance/api/dataGapsApi"
import { useMasterDataStats } from "@/features/material-compliance/api/masterDataStatsApi"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { Database } from "lucide-react"
import { useNavigate } from "react-router"
import { StudioCard, type StudioCardStatState } from "./StudioCard"
import { useNumberFormat } from "./useNumberFormat"

export function MasterDataCard() {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const { format, formatOrNull } = useNumberFormat()
  const { hasPermission } = useUser()
  // HomePage only renders this card behind MC_MASTER_DATA; the permission is
  // the remaining gate, both endpoints 403 without MC_MASTER_DATA_VIEW.
  const canViewMasterData = hasPermission(PERMISSIONS.MC_MASTER_DATA_VIEW)
  const { data, isError } = useMasterDataStats({ enabled: canViewMasterData })
  const gaps = useDataGapsStats({ enabled: canViewMasterData })

  // Products fallback, kept for when the gaps query itself errors.
  const productsStat: StudioCardStatState = isError
    ? "unavailable"
    : data
      ? {
          value: `${format(data.products.total - data.products.complete)} ${translate("MC_HOME_MD_STAT_NOUN")}`,
          caption: translate("MC_HOME_MD_STAT_CAPTION"),
          tone:
            data.products.total - data.products.complete > 0
              ? "warning"
              : "success",
        }
      : "loading"

  // Gap KINDS, not a sum of raw counts: summing articles + products +
  // suppliers would be a meaningless mega-figure dominated by whichever
  // entity has the most rows (challenge finding 15).
  const nonZeroGapLines =
    gaps.data?.lines.filter((line) => line.count > 0) ?? []
  const gapKindsCount = nonZeroGapLines.length
  const anyNonZeroGapBlocking = nonZeroGapLines.some((line) => line.blocking)

  // A disabled query is neither loading nor errored, so without the gate it
  // would read as "0 gap kinds, all good" to someone who cannot see gaps.
  const stat: StudioCardStatState = !canViewMasterData
    ? "unavailable"
    : gaps.isError
      ? productsStat
      : gaps.isLoading
        ? "loading"
        : {
            value: `${gapKindsCount} ${translate("MC_HOME_MD_GAPS_STAT_NOUN")}`,
            caption: translate("MC_HOME_MD_GAPS_STAT_CAPTION"),
            tone: anyNonZeroGapBlocking
              ? "warning"
              : gapKindsCount === 0
                ? "success"
                : "default",
          }

  // Blocking gap KINDS for the corner badge: independent of the front stat
  // above, unchanged from before this card started showing gaps as its stat.
  const blockingGapKinds =
    gaps.data?.lines.filter((line) => line.blocking && line.count > 0).length ??
    0

  const gapBackRows = nonZeroGapLines.map((line) => ({
    label: translate(line.labelKey),
    value: `${line.count} ${translate(line.unitKey)}`,
  }))

  return (
    <StudioCard
      icon={Database}
      title={translate("MC_TAB_MASTER_DATA")}
      question={translate("MC_HOME_MD_QUESTION")}
      stat={stat}
      statCaption={translate("MC_HOME_MD_STAT_CAPTION")}
      roleLabel={translate("MC_HOME_MD_ROLE")}
      badge={
        blockingGapKinds > 0
          ? {
              count: blockingGapKinds,
              tooltip: translate("MC_HOME_MD_GAPS_BADGE_TIP", {
                n: blockingGapKinds,
              }),
            }
          : undefined
      }
      backTitle={translate("MC_HOME_MD_BACK_TITLE")}
      backRows={[
        {
          label: translate("MC_MD_TAB_ARTICLES"),
          value: formatOrNull(data?.articles.total),
        },
        {
          label: translate("MC_MD_TAB_SUPPLIERS"),
          value: formatOrNull(data?.suppliers.total),
        },
        {
          label: translate("MC_MD_TAB_SUBSTANCES"),
          value: formatOrNull(data?.substances.total),
        },
        {
          label: translate("MC_MD_TAB_PRODUCTS"),
          value: formatOrNull(data?.products.total),
        },
        {
          label: translate("MC_MD_TAB_CUSTOMERS"),
          value: formatOrNull(data?.customers.total),
        },
      ]}
      backSubsection={{
        title: translate("MC_MD_GAPS_BOARD_TITLE"),
        rows: gapBackRows,
      }}
      ctaLabel={translate("MC_HOME_MD_CTA")}
      onOpen={() => navigate("/material-compliance/master-data")}
    />
  )
}
